<template>
  <div class="container">
    <a-row :gutter="16">
      <a-col :span="24">
        <VolcanoMap :volcanoes="volcanoes" />
      </a-col>
    </a-row>

    <VolcanoCharts :volcanoes="volcanoes" />

    <a-row :gutter="16">
      <a-col :span="24">
        <a-card title="火山监测列表" :bordered="false">
          <a-table
            :columns="columns"
            :data-source="volcanoes"
            :loading="loading"
            rowKey="id"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'alertLevel'">
                <a-tag :color="getAlertLevelColor(record.status?.alertLevel)">
                  {{ record.status?.alertLevel }}
                </a-tag>
              </template>
              <template v-if="column.key === 'aviationCode'">
                <a-tag :color="getAviationCodeColor(record.status?.aviationColorCode)">
                  {{ record.status?.aviationColorCode }}
                </a-tag>
              </template>
              <template v-if="column.key === 'action'">
                <a-button type="link" @click="showHistory(record)">查看历史</a-button>
              </template>
            </template>
          </a-table>
        </a-card>
      </a-col>
    </a-row>

    <a-modal
      v-model:visible="historyModalVisible"
      title="火山状态历史记录"
      width="800px"
      :footer="null"
    >
      <a-timeline>
        <a-timeline-item
          v-for="status in selectedVolcanoHistory"
          :key="status.id"
          :color="getTimelineColor(status.alertLevel)"
        >
          <p>{{ formatDateTime(status.statusTime) }}</p>
          <p>警戒级别: {{ status.alertLevel }}</p>
          <p>航空代码: {{ status.aviationColorCode }}</p>
          <p>{{ status.statusDescription }}</p>
        </a-timeline-item>
      </a-timeline>
    </a-modal>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import axios from 'axios';
import moment from 'moment';
import VolcanoMap from '../components/VolcanoMap.vue';
import VolcanoCharts from '../components/VolcanoCharts.vue';
import { mockVolcanoes } from '@/mock/volcanoes';

export default defineComponent({
  name: 'Home',
  components: {
    VolcanoMap,
    VolcanoCharts,
  },
  setup() {
    const columns = [
      {
        title: '火山名称',
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: '位置',
        dataIndex: 'location',
        key: 'location',
      },
      {
        title: '警戒级别',
        key: 'alertLevel',
      },
      {
        title: '航空代码',
        key: 'aviationCode',
      },
      {
        title: '操作',
        key: 'action',
      },
    ];

    const loading = ref(false);
    const volcanoes = ref(mockVolcanoes);
    const historyModalVisible = ref(false);
    const selectedVolcanoHistory = ref([]);

    const loadVolcanoes = async () => {
      loading.value = true;
      try {
        const [volcanoesRes, statusRes] = await Promise.all([
          axios.get('http://localhost:8080/api/volcanoes'),
          axios.get('http://localhost:8080/api/volcanoes/status'),
        ]);
        
        volcanoes.value = volcanoesRes.data.map((volcano: any) => ({
          ...volcano,
          status: statusRes.data[volcano.id],
        }));
      } catch (error) {
        console.error('Failed to load volcanoes:', error);
      } finally {
        loading.value = false;
      }
    };

    const showHistory = async (record: any) => {
      try {
        const response = await axios.get(`http://localhost:8080/api/volcanoes/${record.id}/history`);
        selectedVolcanoHistory.value = response.data;
        historyModalVisible.value = true;
      } catch (error) {
        console.error('Failed to load history:', error);
      }
    };

    const getAlertLevelColor = (level: string) => {
      const colors: Record<string, string> = {
        NORMAL: 'green',
        ADVISORY: 'blue',
        WATCH: 'orange',
        WARNING: 'red',
      };
      return colors[level] || 'default';
    };

    const getAviationCodeColor = (code: string) => {
      const colors: Record<string, string> = {
        GREEN: 'green',
        YELLOW: 'yellow',
        ORANGE: 'orange',
        RED: 'red',
      };
      return colors[code] || 'default';
    };

    const getTimelineColor = (level: string) => {
      return getAlertLevelColor(level);
    };

    const formatDateTime = (datetime: string) => {
      return moment(datetime).format('YYYY-MM-DD HH:mm:ss');
    };

    onMounted(() => {
      loadVolcanoes();
    });

    return {
      columns,
      loading,
      volcanoes,
      historyModalVisible,
      selectedVolcanoHistory,
      showHistory,
      getAlertLevelColor,
      getAviationCodeColor,
      getTimelineColor,
      formatDateTime,
    };
  },
});
</script>

<style scoped>
.container {
  padding: 24px;
}
</style>
